import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { Aws, S3UploadResponse } from 'src/providers/aws/aws';
import { randomUUID } from 'src/tidy-ui-components/utils/secure-utils';

import { environment } from 'src/environments/environment';

/**
 * A photo on one guidebook card. `card_key` is the card's id on the guidebook page:
 * the setting key for text and custom cards, the category id for category cards,
 * and `wifi` for the standalone WiFi card.
 */
export interface GuidebookCardPhoto {
  id: number;
  card_key: string;
  order?: number;
  photo_url: string;
  created_by_id?: number;
  created_by_type?: string;
  created_at?: string;
  updated_at?: string;
}

/** What the file picker hands back for each chosen image. */
export interface PickedImage {
  uuid?: string;
  url: string;
  file?: unknown;
  exif?: unknown;
}

export function groupPhotosByCard(photos: GuidebookCardPhoto[]): Record<string, GuidebookCardPhoto[]> {
  const byCard: Record<string, GuidebookCardPhoto[]> = {};
  for (const photo of photos || []) {
    (byCard[photo.card_key] ??= []).push(photo);
  }
  return byCard;
}

@Injectable({ providedIn: 'root' })
export class GuidebookPhotosService {

  private http = inject(HttpClient);
  private aws = inject(Aws);

  async listPhotos(addressId: number): Promise<GuidebookCardPhoto[]> {
    return firstValueFrom(this.http.get<GuidebookCardPhoto[]>(this.baseUrl(addressId)));
  }

  /** Uploads the picked image to the S3 upload folder, then records it on the card with that URL. */
  async addPhoto(addressId: number, cardKey: string, image: PickedImage): Promise<GuidebookCardPhoto> {
    const key = `guidebook-photos/${addressId}/${cardKey}/${image.uuid || randomUUID()}.jpg`;
    const upload: S3UploadResponse = await this.aws.uploadImageToS3(image.url, key, image.file, image.exif);
    const body = { card_key: cardKey, photo_url: upload.Location };
    return firstValueFrom(this.http.post<GuidebookCardPhoto>(this.baseUrl(addressId), body));
  }

  async deletePhoto(addressId: number, photoId: number): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.baseUrl(addressId)}/${photoId}`));
  }

  private baseUrl(addressId: number): string {
    return `${environment.api_url}api/v1/customer/addresses/${addressId}/guidebook-photos`;
  }
}
